import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ExternalLink, ChevronUp, ChevronDown, ChevronsUpDown, X, Bug, ArrowUp, Puzzle, Feather, Flame, Siren, Tag } from 'lucide-react';

const JIRA_BASE = 'https://contentsquare.atlassian.net/browse';
const LS_KEY = 'campaigns_issues_columns';
const LS_SORT_KEY = 'campaigns_issues_sort';
const DEFAULT_SORT = { col: 'severity', dir: 'desc' };

function formatRelative(isoString, now) {
  if (!isoString) { return null; }
  const diffMs = now - new Date(isoString);
  const diffS  = Math.floor(diffMs / 1000);
  if (diffS < 60)  { return `${diffS}s ago`; }
  const diffM = Math.floor(diffS / 60);
  if (diffM < 60)  { return `${diffM}m ago`; }
  const diffH = Math.floor(diffM / 60);
  if (diffH < 24)  { return `${diffH}h ago`; }
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7)   { return `${diffD}d ago`; }
  const diffW = Math.floor(diffD / 7);
  if (diffW < 5)   { return `${diffW}w ago`; }
  const diffMo = Math.floor(diffD / 30);
  return `${diffMo}mo ago`;
}

function formatExact(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function severityRank(val) {
  const lower = (val ?? '').toLowerCase();
  if (lower.includes('critical')) return 3;
  if (lower.includes('major')) return 2;
  if (lower.includes('minor')) return 1;
  return 0;
}

function loadSort() {
  try {
    const stored = localStorage.getItem(LS_SORT_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return DEFAULT_SORT;
}

function statusClass(status) {
  if (!status) return 'default';
  if (status === 'Done') return 'done';
  if (status === 'Backlog') return 'backlog';
  return 'default';
}

const COLUMNS = [
  { label: 'Key',           key: 'key',          required: true },
  { label: 'Title',         key: 'summary',      required: true },
  { label: 'Severity',      key: 'severity',     defaultHidden: true },
  { label: 'Component',     key: 'component',    defaultHidden: true },
  { label: 'Unit',          key: 'unit',         defaultHidden: true },
  { label: 'Status',        key: 'status' },
  { label: 'Reporter',      key: 'reporter' },
  { label: 'Assignee',      key: 'assignee' },
  { label: 'Review Status', key: 'reviewStatus' },
  { label: 'Resolution',    key: 'resolution' },
  { label: 'Updated',       key: 'updatedAt' },
];

function loadVisibleColumns() {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) {
      const keys = JSON.parse(stored);
      const required = COLUMNS.filter(c => c.required).map(c => c.key);
      return [...new Set([...required, ...keys])];
    }
  } catch {}
  return COLUMNS.filter(c => !c.defaultHidden).map(c => c.key);
}

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown size={12} className="sort-icon inactive" />;
  return sortDir === 'asc'
    ? <ChevronUp size={12} className="sort-icon" />
    : <ChevronDown size={12} className="sort-icon" />;
}

function ColumnSelector({ visibleKeys, onChange, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  function toggle(key) {
    const col = COLUMNS.find(c => c.key === key);
    if (col?.required) return;
    const next = visibleKeys.includes(key)
      ? visibleKeys.filter(k => k !== key)
      : [...visibleKeys, key];
    onChange(next);
  }

  return (
    <div className="column-selector-popup" ref={ref}>
      <div className="column-selector-header">
        <span>Columns</span>
        <button className="column-selector-close" onClick={onClose}><X size={14} /></button>
      </div>
      {COLUMNS.map(col => (
        <label key={col.key} className={`column-selector-item${col.required ? ' required' : ''}`}>
          <input
            type="checkbox"
            checked={visibleKeys.includes(col.key)}
            disabled={col.required}
            onChange={() => toggle(col.key)}
          />
          {col.label}
        </label>
      ))}
    </div>
  );
}

function IssuesTable({ issuesTable, showColumnSelector, onCloseColumnSelector }) {
  const [sortCol, setSortCol] = useState(() => loadSort().col);
  const [sortDir, setSortDir] = useState(() => loadSort().dir);
  const [visibleKeys, setVisibleKeys] = useState(loadVisibleColumns);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  function handleSort(key) {
    const newDir = sortCol === key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortCol(key);
    setSortDir(newDir);
    try { localStorage.setItem(LS_SORT_KEY, JSON.stringify({ col: key, dir: newDir })); } catch {}
  }

  function handleColumnChange(keys) {
    setVisibleKeys(keys);
    try { localStorage.setItem(LS_KEY, JSON.stringify(keys)); } catch {}
  }

  const visibleCols = COLUMNS.filter(c => visibleKeys.includes(c.key));

  const sorted = useMemo(() => {
    return [...issuesTable].sort((a, b) => {
      if (sortCol === 'severity') {
        const diff = severityRank(a.severity) - severityRank(b.severity);
        return sortDir === 'asc' ? diff : -diff;
      }
      if (sortCol === 'updatedAt') {
        const at = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bt = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return sortDir === 'asc' ? at - bt : bt - at;
      }
      const av = (a[sortCol] ?? '').toString().toLowerCase();
      const bv = (b[sortCol] ?? '').toString().toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [issuesTable, sortCol, sortDir]);

  return (
    <div style={{ position: 'relative' }}>
      {showColumnSelector && (
        <ColumnSelector
          visibleKeys={visibleKeys}
          onChange={handleColumnChange}
          onClose={onCloseColumnSelector}
        />
      )}
      <div className="issues-table-wrap">
        <table className="issues-table">
          <thead>
            <tr>
              {visibleCols.map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} className="sortable-th">
                  {col.label}
                  <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(issue => (
              <tr key={issue.key}>
                {visibleCols.map(col => {
                  switch (col.key) {
                    case 'key':
                      return (
                        <td key="key" style={{ whiteSpace: 'nowrap' }}>
                          <a href={`${JIRA_BASE}/${issue.key}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--cmp-primary)', fontWeight: 600, textDecoration: 'none' }}>
                            <ExternalLink size={15} />
                            {issue.key}
                          </a>
                        </td>
                      );
                    case 'summary': {
                      const typePill = issue.issueType === 'Bug'
                        ? <span className="issue-type-pill bug"><Bug size={11} />Bug</span>
                        : issue.issueType === 'Improvement'
                        ? <span className="issue-type-pill improvement"><ArrowUp size={11} />Improvement</span>
                        : null;
                      const sev = (issue.severity ?? '').toLowerCase();
                      const showSeverityPill = !visibleKeys.includes('severity');
                      const severityPill = showSeverityPill && (sev.includes('critical')
                        ? <span className="issue-type-pill severity-critical"><Siren size={11} />Critical</span>
                        : sev.includes('major')
                        ? <span className="issue-type-pill severity-major"><Flame size={11} />Major</span>
                        : sev.includes('minor')
                        ? <span className="issue-type-pill severity-minor"><Feather size={11} />Minor</span>
                        : null);
                      return (
                        <td key="summary">
                          <div className="issue-cell-link">
                            <div className="issue-cell-title">{issue.summary}</div>
                            {(() => {
                              const showComponentPills = !visibleKeys.includes('component');
                              const componentPills = showComponentPills && issue.component
                                ? issue.component
                                    .split(',')
                                    .map(c => c.trim().replace(/^"|"$/g, ''))
                                    .filter(Boolean)
                                    .map(c => (
                                      <span key={c} className="issue-type-pill component">
                                        <Puzzle size={11} />
                                        {c}
                                      </span>
                                    ))
                                : null;
                              const showUnitPill = !visibleKeys.includes('unit');
                              const unitPill = showUnitPill && issue.unit
                                ? <span className="issue-type-pill unit"><Tag size={11} />{issue.unit}</span>
                                : null;
                              return (typePill || severityPill || componentPills || unitPill) && (
                                <div className="issue-cell-component">
                                  {typePill}
                                  {severityPill}
                                  {componentPills}
                                  {unitPill}
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                      );
                    }
                    case 'severity': {
                      const s = (issue.severity ?? '').toLowerCase();
                      return (
                        <td key="severity">
                          {s.includes('critical')
                            ? <span className="issue-type-pill severity-critical"><Siren size={11} />Critical</span>
                            : s.includes('major')
                            ? <span className="issue-type-pill severity-major"><Flame size={11} />Major</span>
                            : s.includes('minor')
                            ? <span className="issue-type-pill severity-minor"><Feather size={11} />Minor</span>
                            : '—'}
                        </td>
                      );
                    }
                    case 'component': {
                      const parts = issue.component
                        ? issue.component.split(',').map(c => c.trim().replace(/^"|"$/g, '')).filter(Boolean)
                        : [];
                      return (
                        <td key="component">
                          {parts.length
                            ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {parts.map(c => (
                                  <span key={c} className="issue-type-pill component">
                                    <Puzzle size={11} />
                                    {c}
                                  </span>
                                ))}
                              </div>
                            : '—'}
                        </td>
                      );
                    }
                    case 'unit':
                      return (
                        <td key="unit">
                          {issue.unit
                            ? <span className="issue-type-pill unit"><Tag size={11} />{issue.unit}</span>
                            : '—'}
                        </td>
                      );
                    case 'status':     return <td key="status"><span className={`status-chip ${statusClass(issue.status)}`}>{issue.status}</span></td>;
                    case 'reporter':   return <td key="reporter">{issue.reporter}</td>;
                    case 'assignee':   return <td key="assignee">{issue.assignee}</td>;
                    case 'reviewStatus': return <td key="reviewStatus">{issue.reviewStatus ? issue.reviewStatus.replace(/^Review\s+/i, '').replace(/^./, c => c.toUpperCase()) : '—'}</td>;
                    case 'resolution': return (
                      <td key="resolution">
                        {issue.resolution
                          ? <span className={`status-chip ${issue.resolution === 'Done' ? 'done' : 'resolution-unresolved'}`}>{issue.resolution}</span>
                          : '—'}
                      </td>
                    );
                    case 'updatedAt':
                      return (
                        <td key="updatedAt" title={formatExact(issue.updatedAt)} style={{ whiteSpace: 'nowrap', color: 'var(--cmp-muted)', fontSize: '0.85em' }}>
                          {formatRelative(issue.updatedAt, now) ?? '—'}
                        </td>
                      );
                    default: return <td key={col.key} />;
                  }
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default IssuesTable;
